
import React from "react";
import { motion } from "framer-motion";
import { Layout } from "@/components/Layout";
import { Hero } from "@/components/Hero";
import { UpcomingClasses } from "@/components/UpcomingClasses";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const Index = () => {
  const { t } = useTranslation();
  
  return (
    <Layout>
      <Hero />
      
      <UpcomingClasses />
      
      {/* Features Section */}
      <section className="py-20 bg-secondary/50">
        <div className="container max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">{t('features.heading')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: t('features.expertInstructors.title'),
                description: t('features.expertInstructors.description'),
                icon: "👨‍🏫",
              },
              {
                title: t('features.smallClasses.title'),
                description: t('features.smallClasses.description'),
                icon: "👥",
              },
              {
                title: t('features.materials.title'),
                description: t('features.materials.description'),
                icon: "🧰",
              },
              {
                title: t('features.beginnerFriendly.title'),
                description: t('features.beginnerFriendly.description'),
                icon: "🌱",
              },
              {
                title: t('features.flexibleScheduling.title'),
                description: t('features.flexibleScheduling.description'),
                icon: "📅",
              },
              {
                title: t('features.takeHome.title'),
                description: t('features.takeHome.description'),
                icon: "🏺",
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-background border border-border rounded-xl p-6 hover-scale"
              >
                <div className="w-12 h-12 bg-clay-100 rounded-full flex items-center justify-center text-2xl mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-display font-medium mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <div className="absolute top-20 left-[20%] w-64 h-64 rounded-full bg-terracotta-200 blur-3xl" />
          <div className="absolute bottom-20 right-[30%] w-72 h-72 rounded-full bg-clay-200 blur-3xl" />
        </div>
        
        <div className="container max-w-5xl mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">{t('home_cta.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('home_cta.description')}
            </p>
            <Link to="/booking">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                {t('hero.cta')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
